import { Directory, Filesystem } from "@capacitor/filesystem";
import MultimediaMobileHelper from "./MultimediaMobileHelper";
import { Share } from "@capacitor/share";
import JSZip, { files } from "jszip";
import { random } from "lodash";
import { Capacitor } from "@capacitor/core";
import { BlobServiceClient } from "@azure/storage-blob";
import { saveAs } from "file-saver";
import { VideoEditor } from "@whiteguru/capacitor-plugin-video-editor";
export default class MultimediaHelper {
  static sasToken =
    "sv=2022-11-02&ss=b&srt=sco&sp=rwdlactfx&se=2030-07-05T19:59:33Z&st=2024-07-05T11:59:33Z&spr=https,http&sig=5GboONytsEL8EyTgy1htKc4lsUNbrSNQauJ%2B2Hi9VlE%3D";
  static accountName = "eqappdata";
  static FOLDER_MULTIMEDIA = "multimedia";
  static FOLDER_RESULT = "result";
  static FOLDER_NOTES = "notes";
  static FOLDER_EXTRA_NOTES = "textnote";
  static FOLDER_TASK = "task";
  static FOLDER_GENERAL_TASK = "generalTask";
  static FOLDER_OTHER = "other";

  static TYPE_IMAGE = 1;
  static TYPE_VIDEO = 2;
  static TYPE_YOUTUBE = 3;
  static TYPE_PDF = 4;

  static downloadZip = async (fileUrls) => {
    const zip = new JSZip();

    const fetchFile = async (url) => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}`);
      }
      return response.blob();
    };

    const promises = fileUrls.map(async (item) => {
      const blob = await fetchFile(item.url);
      const fileName = item.name;
      zip.file(fileName, blob);
    });

    try {
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "files.zip");
    } catch (error) {
      console.error("Error creating zip file:", error);
    }
  };
  static downloadFilesOneByOne = async (images) => {
    const fetchImage = async (image) => {
      const response = await fetch(image.url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${image.url}`);
      }
      const blob = await response.blob();
      return { blob, name: image.name }; // Extract the file name from the URL
    };

    try {
      const promises = images.map(fetchImage);
      const fetchedImages = await Promise.all(promises);

      fetchedImages.forEach(({ blob, name }) => {
        saveAs(blob, name);
      });
    } catch (error) {
      console.error("Error downloading images:", error);
    }
  };

  constructor() {
    // queue elements to be uploaded
    window.globalMultimediaHelper.isUploading = false;
    this.urisToShare = [];
    this.id = random(true) * 50;

    this.dropbox = new window.Dropbox.Dropbox({
      accessToken: MultimediaHelper.DropboxToken,
    });

    if (!MultimediaHelper.sasToken || !MultimediaHelper.accountName) {
      console.error("Azure Storage SAS token or account name not found");
      return;
    }

    this.blobServiceClient = new BlobServiceClient(
      `https://${MultimediaHelper.accountName}.blob.core.windows.net?${MultimediaHelper.sasToken}`
    );
  }

  handleCreateContainer = async (containerName) => {
    try {
      this.containerClient =
        this.blobServiceClient.getContainerClient(containerName);
      await this.containerClient.create();
      console.log(`Container "${containerName}" created successfully.`);
    } catch (error) {
      console.error(`Error creating container: ${error.message}`);
    }
  };

  listBlobsWithProperties = async (containerName) => {
    const containerClient =
      this.blobServiceClient.getContainerClient(containerName);

    let blobs = [];
    for await (const blob of containerClient.listBlobsFlat()) {
      const blockBlobClient = containerClient.getBlockBlobClient(blob.name);
      const properties = await blockBlobClient.getProperties();

      const url = `https://${MultimediaHelper.accountName}.blob.core.windows.net/${containerName}/${blob.name}?${MultimediaHelper.sasToken}`;
      blobs.push({
        name: blob.name,
        size: properties.contentLength,
        lastModifiedDate: properties.lastModified,
        url: url,
      });
    }

    return blobs;
  };

  listFilesInContainer = async (containerName) => {
    this.containerClient =
      this.blobServiceClient.getContainerClient(containerName);
    let blobItems = [];
    for await (const blob of this.containerClient.listBlobsFlat()) {
      if (blob.name.length > 1024) {
        throw new Error(
          `Blob name "${blob.name}" exceeds the maximum length of 1024 characters.`
        );
      }
      const blobUrl = this.containerClient.getBlobClient(blob.name).url;
      blobItems.push({ name: blob.name, url: blobUrl });
    }
    return blobItems;
  };

  /**
   * Setter for the current multimedia session.
   */
  setCurrentSettings(type, horseId, userId, multimediaPage, noteOrTaskId) {
    this.type = type;
    this.horseId = horseId;
    this.userId = userId;
    this.multimediaPage = multimediaPage;
    this.noteOrTaskId = noteOrTaskId;
    this.dropbox = new window.Dropbox.Dropbox({
      accessToken: MultimediaHelper.DropboxToken,
    });
    this.path = this.getPath(type).toLowerCase();
    this.containerClient = this.blobServiceClient.getContainerClient(this.path);

    //subscribe to updates for my set
    //subscribe to the upload queue

    window.globalMultimediaHelper.subscribeToUpload(
      this.path,
      this.finishCallback,
      this.progressCallback,
      this.refreshUploadQueue
    );

    // queue elements to be uploaded
  }
  setRefreshCallback(callback) {
    this.refreshUploadQueue = callback;
  }

  blobToBase64(blob) {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  mimeToExtension(mimeType) {
    const mimeMap = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/gif": "gif",
      "application/pdf": "pdf",
      // Add other mappings as needed
    };
    return mimeMap[mimeType] || ""; // Return empty string if not found
  }

  shareItems = async (items, text) => {
    const promises = items.map(async (item) => {
      const { url } = item;
      const response = await fetch(url);
      const blob = await response.blob();
      const mimeType = blob.type;
      const extension = mimeType.split("/")[1];
      const base64Data = await this.blobToBase64(blob);
      let fileName = item.name;
      if (!fileName.includes(".")) {
        fileName += `.${extension}`;
      }
      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
      });
      return savedFile.uri;
    });

    const localFiles = await Promise.all(promises);
    await Share.share({
      title: "Share files",
      text: text || "",
      files: localFiles,
      dialogTitle: "Share with",
    });
    window.Sprinner.hide();
  };

  /**
   * Setter for the callback function
   * @param {Function<>()} finishCallback The new finish callback function
   */
  setFinishCallback(finishCallback) {
    this.finishCallback = finishCallback;
  }

  /**
   * Setter for the progress callback function
   * @param {Function<>()} progressCallback The new progress callback function
   */
  setProgressCallback(progressCallback) {
    this.progressCallback = progressCallback;
  }

  /**
   * A function that adds files to the upload queue.
   *
   * @param {Array<object>} files  List of files to be added to the upload queue.
   */
  addToUploadQueue(files, static_path) {
    window.globalMultimediaHelper.addToUploadQueue(
      this.containerClient.containerName,
      files,
      this.path,
      this.finishCallback,
      this.progressCallback
    );
  }

  /**
   * A function that returns all the elements in the upload queue such that the origin or the items matches the origin of the requester.
   *
   * @returns a list of items from upload list that match the requester's origin.
   */
  loadUploadQueue = () => {
    return window.globalMultimediaHelper.uploadQueue.filter((item) =>
      item.containerName.startsWith(this.containerClient.containerName)
    );
  };

  getPath = (type) => {
    switch (type) {
      case MultimediaHelper.FOLDER_MULTIMEDIA:
        return `${MultimediaHelper.FOLDER_MULTIMEDIA}${this.horseId}`;
      case MultimediaHelper.FOLDER_EXTRA_NOTES:
        return `${this.type}_${this.noteOrTaskId}`;
      case MultimediaHelper.FOLDER_TASK:
        let horse = window.DataStorage.getAllHorses().find(
          (h) => h.id == this.horseId
        );
        return `${this.type}=${horse.userid}-${this.noteOrTaskId}`;

      case MultimediaHelper.FOLDER_GENERAL_TASK:
        return `${this.type}=${this.noteOrTaskId}`;

      default:
        return `${this.type}-${this.horseId}`;
    }
  };

  loadList = (callback, type) => {
    this.loadCallback = callback;
    this.path = this.getPath(type).toLowerCase();
    this.handleCreateContainer(this.path)
      .then((response) => {
        if (this.type == MultimediaHelper.FOLDER_MULTIMEDIA) {
          this._getListFilesForMultimedia();
        } else if (
          this.type == MultimediaHelper.FOLDER_TASK &&
          this.multimediaPage
        ) {
          this._getListFilesForTasks();
        } else if (
          this.type == MultimediaHelper.FOLDER_NOTES &&
          this.multimediaPage
        ) {
          this._getListFilesForNotes();
        } else {
          this._getListFiles();
        }
      })
      .catch((error) => {
        if (this.type == MultimediaHelper.FOLDER_MULTIMEDIA) {
          this._getListFilesForMultimedia();
        } else if (
          this.type == MultimediaHelper.FOLDER_TASK &&
          this.multimediaPage
        ) {
          this._getListFilesForTasks();
        } else if (
          this.type == MultimediaHelper.FOLDER_NOTES &&
          this.multimediaPage
        ) {
          this._getListFilesForNotes();
        } else {
          this._getListFiles();
        }
      });
  };
  generateThumbnail(videoUrl, seekTime = 1) {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous"; // Ensure the video is fetched with CORS headers

      video.src = videoUrl;
      video.preload = "metadata";
      video.currentTime = 0.001;

      video.addEventListener("seeked", () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext("2d");

        // Draw the video frame to the canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Get the image data URL from the canvas
        const imageUrl = canvas.toDataURL("image/png");
        resolve(imageUrl);
      });

      video.addEventListener("error", (error) => {
        resolve(videoUrl);
        // reject(`Error loading video: ${error.message}`);
      });
    });
  }

  _getListFiles = () => {
    this.listFilesInContainer(this.path)
      .then(async (response) => {
        let result = [];
        for (let el of response) {
          el.path_display = el.name;
          el.fullUrl = el.url;
          el.thumbUrl =
            MultimediaHelper.getTypeItem(el) === MultimediaHelper.TYPE_VIDEO // Capacitor.getPlatform() === "web"
              ? await this.generateThumbnail(
                  // "http://localhost:8000/yt5s.io-Cargo%20-%20Ielele%20(Official%20Audio).mp4"
                  el.url
                )
              : el.url;
          el.imageUrl = el.url;
          el.type = MultimediaHelper.getTypeItem(el);
          result.push(el);
        }
        this.loadCallback(result);
        return;
        if (!response.entries) return;
        let items = [];
        for (let i = 0; i < response.entries.length; i++) {
          let item = response.entries[i];
          item.type = MultimediaHelper.getTypeItem(item);
          items.push(item);
        }
        items = items.sort(function (a, b) {
          if (a["server_modified"] > b["server_modified"]) return -1;
          if (a["server_modified"] < b["server_modified"]) return 1;
          return 0;
        });

        if (items.length != 0) {
          this._buildThumbs(items);
        } else {
          this.loadCallback(items);
        }
      })

      .catch((error) => {
        this.loadCallback(null);
      });
  };

  _getListFilesForMultimedia = () => {
    console.warn(this.path);
    this.listFilesInContainer({ path: this.path })
      .then((response) => {
        console.log(response);

        this.loadCallback(response);
        // }
        return;
        window.DataGate.getYoutubeMultimedia(
          this.userId,
          this.horseId,
          (code, youtubeData) => {
            if (!response.entries) return;
            let items = [];
            for (let i = 0; i < response.entries.length; i++) {
              let item = response.entries[i];
              item.type = MultimediaHelper.getTypeItem(item);
              //let ext = item.path_display.split('.').pop().toLowerCase();
              //if (ext == "png" || ext == "jpeg" || ext == "jpg" || ext == "gif" || ext == "bmp") {
              //    item.type = MultimediaHelper.TYPE_IMAGE;
              //} else {
              //    item.type = MultimediaHelper.TYPE_VIDEO;
              //}
              items.push(item);
            }
            items = items.sort(function (a, b) {
              if (a["server_modified"] > b["server_modified"]) return -1;
              if (a["server_modified"] < b["server_modified"]) return 1;
              return 0;
            });
            if (youtubeData != null) {
              for (let i = 0; i < youtubeData.length; i++) {
                let item = youtubeData[i];
                item.type = MultimediaHelper.TYPE_YOUTUBE;
                items.push(item);
              }
            }

            if (items.length != 0) {
              this._buildThumbs(items);
            } else {
              this.loadCallback(items);
            }
          }
        );
      })
      .catch((error) => {
        this.loadCallback(null);
      });
  };

  _getListFilesForTasks = () => {
    let end = new Date().formatDesh();
    let start = new Date().addDays(-92).formatDesh();
    window.db.getTasksByHorseIdForMultimedia(
      this.horseId,
      start,
      end,
      (taskArr) => {
        let horse = window.DataStorage.getAllHorses().find(
          (h) => h.id == this.horseId
        );
        let horseOwnerId = horse.userid;
        let items = [];
        if (!taskArr.length) this.loadCallback(items);
        for (let i = 0; i < taskArr.length; i++) {
          this.path = "Task" + horseOwnerId + "-" + taskArr[i]["Id"];
          this.dropbox
            .filesListFolder({ path: this.path })
            .then((response) => {
              if (!response.entries) return;

              for (let i = 0; i < response.entries.length; i++) {
                let item = response.entries[i];
                item.type = MultimediaHelper.getTypeItem(item);
                //let ext = item.path_display.split('.').pop().toLowerCase();
                //if (ext == "png" || ext == "jpeg" || ext == "jpg" || ext == "gif" || ext == "bmp") {
                //    item.type = MultimediaHelper.TYPE_IMAGE;
                //} else {
                //    item.type = MultimediaHelper.TYPE_VIDEO;
                //}
                items.push(item);
              }
              items = items.sort(function (a, b) {
                if (a["server_modified"] > b["server_modified"]) return -1;
                if (a["server_modified"] < b["server_modified"]) return 1;
                return 0;
              });

              if (i == taskArr.length - 1) {
                setTimeout(() => {
                  if (items.length != 0) {
                    this._buildThumbs(items);
                  } else {
                    this.loadCallback(items);
                  }
                }, 500);
              }
            })
            .catch((error) => {
              this.loadCallback(null);
            });
        }
      }
    );
  };

  _getListFilesForNotes = () => {
    window.db.getNotesByHorse(this.horseId, (notesArr) => {
      let items = [];
      this.dropbox
        .filesListFolder({ path: this.path })
        .then((response) => {
          if (!response.entries) return;
          for (let i = 0; i < response.entries.length; i++) {
            let item = response.entries[i];
            item.type = MultimediaHelper.getTypeItem(item);
            //let ext = item.path_display.split('.').pop().toLowerCase();
            //if (ext == "png" || ext == "jpeg" || ext == "jpg" || ext == "gif" || ext == "bmp") {
            //    item.type = MultimediaHelper.TYPE_IMAGE;
            //} else {
            //    item.type = MultimediaHelper.TYPE_VIDEO;
            //}
            items.push(item);
          }
          items = items.sort(function (a, b) {
            if (a["server_modified"] > b["server_modified"]) return -1;
            if (a["server_modified"] < b["server_modified"]) return 1;
            return 0;
          });
          if (!notesArr.length) this.loadCallback(items);
          for (let i = 0; i < notesArr.length; i++) {
            this.path =
              `${MultimediaHelper.FOLDER_EXTRA_NOTES}-` +
              this.horseId +
              "-" +
              notesArr[i]["Id"];
            this.dropbox
              .filesListFolder({ path: this.path })
              .then((response) => {
                if (!response.entries) return;

                for (let i = 0; i < response.entries.length; i++) {
                  let item = response.entries[i];
                  item.type = MultimediaHelper.getTypeItem(item);
                  //let ext = item.path_display.split('.').pop().toLowerCase();
                  //if (ext == "png" || ext == "jpeg" || ext == "jpg" || ext == "gif" || ext == "bmp") {
                  //    item.type = MultimediaHelper.TYPE_IMAGE;
                  //} else {
                  //    item.type = MultimediaHelper.TYPE_VIDEO;
                  //}
                  items.push(item);
                }
                items = items.sort(function (a, b) {
                  if (a["server_modified"] > b["server_modified"]) return -1;
                  if (a["server_modified"] < b["server_modified"]) return 1;
                  return 0;
                });

                if (i == notesArr.length - 1) {
                  setTimeout(() => {
                    if (items.length != 0) {
                      this._buildThumbs(items);
                    } else {
                      this.loadCallback(items);
                    }
                  }, 500);
                }
              })
              .catch((error) => {
                this.loadCallback(null);
              });
          }
        })
        .catch((error) => {
          this.loadCallback(null);
        });
    });
  };

  _checkAllThumbLoaded = () => {
    let notLoadedItems = this.items.filter((item) => item.thumbUrl == null);
    if (notLoadedItems.length == 0) {
      this.loadCallback(this.items);
    }
  };

  _buildThumbs = (items) => {
    this.items = items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type == MultimediaHelper.TYPE_YOUTUBE) {
        let code = this._getYoutubeCode(items[i].Link);
        items[i].thumbUrl = `https://img.youtube.com/vi/${code}/hqdefault.jpg`;
        items[i].fullUrl = `https://www.youtube.com/embed/${code}`;
      } else {
        this._loadThumb(items[i]);
      }
    }
    this._checkAllThumbLoaded();
  };

  _getYoutubeCode = (link) => {
    //https://youtu.be/tezlfdvAzcc
    //https://www.youtube.com/watch?v=M_hVFRTzFw8

    if (link.indexOf("youtu.be") != -1) {
      let code = link.split("/").pop();
      return code;
    }
    if (link.indexOf("?v=") != -1) {
      let code = link.split("?v=").pop();
      return code;
    }
    return null;
  };

  _loadThumb = (item) => {
    new Promise((resolve, reject) => {
      this.dropbox
        .filesGetThumbnail({
          path: item.path_display,
          size: "w480h320",
        })
        .then((response) => {
          let url = window.URL.createObjectURL(response.fileBlob);
          item.thumbUrl = url;
          this._checkAllThumbLoaded();
        })
        .catch((error) => {
          item.thumbUrl = "";
          this._checkAllThumbLoaded();
        });
    });
  };

  getItemLink = (item, callback) => {
    if (item.fullUrl != null) {
      if (callback) callback(item.fullUrl);
      return;
    }
    if (item.type == MultimediaHelper.TYPE_YOUTUBE) {
      let code = this._getYoutubeCode(item);
      item.fullUrl = `https://www.youtube.com/embed/${code}`;
      if (callback) callback(item.fullUrl);
      return;
    }

    this.dropbox
      .filesGetTemporaryLink({ path: item.path_display })
      .then((response) => {
        item.fullUrl = response.link;
        if (callback) callback(item.fullUrl);
      })
      .catch(function (error) {
        if (callback) callback(null);
      });
  };

  wait(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  cancelUploading = (fileName) => {
    let cancelIndex = window.globalMultimediaHelper.uploadQueue.findIndex(
      (f) => f.file.fileName == fileName
    );
    window.globalMultimediaHelper.splice(cancelIndex, 1);

    this.refreshUploadQueue();
  };

  uploadYoutube = (link, callback) => {
    window.DataGate.addYoutubeMultimedia(
      link,
      this.userId,
      this.horseId,
      (c, data) => {
        let code = this._getYoutubeCode(link);
        const item = {
          Link: link,
          type: MultimediaHelper.TYPE_YOUTUBE,
          fullUrl: `https://www.youtube.com/embed/${code}`,
          thumbUrl: `https://img.youtube.com/vi/${code}/hqdefault.jpg`,
        };
        callback(item);
      }
    );
  };

  deleteItems = (items, callback) => {
    this.deleteCallback = callback;
    this.itemsForDelete = items;
    for (let i = 0; i < this.itemsForDelete.length; i++) {
      this._deleteItem(this.itemsForDelete[i]);
    }
  };

  _deleteItem = (item) => {
    if (item.type == MultimediaHelper.TYPE_YOUTUBE) {
      window.DataGate.deleteYoutubeMultimedia(item.Id, () => {
        item.isDeleted = true;
        this._checkItemsDelete();
      });
    } else {
      const blobClient = this.containerClient.getBlobClient(item.name);

      // Delete the blob
      blobClient.delete().then(() => {
        item.isDeleted = true;
        this._checkItemsDelete();
      });
      // this.dropbox
      //   .filesDelete({ path: item.path_display })
      //   .then((response) => {
      //     item.isDeleted = true;
      //     this._checkItemsDelete();
      //   })
      //   .catch((error) => {
      //     console.error(error);
      //     item.isDeleted = true;
      //     this._checkItemsDelete();
      //   });
    }
  };

  _checkItemsDelete = () => {
    let notDeletedItems = this.itemsForDelete.filter((item) => !item.isDeleted);
    if (notDeletedItems.length == 0) {
      this.deleteCallback();
    }
  };

  changeName = async (oldFileName, newFileName, callback) => {
    const oldBlobClient = this.containerClient.getBlobClient(oldFileName);
    const newBlobClient = this.containerClient.getBlobClient(newFileName);

    // Copy the blob to the new name
    newBlobClient.beginCopyFromURL(oldBlobClient.url).then(() =>
      oldBlobClient
        .delete()
        .then(() => callback(true))
        .catch(() => callback(false))
    );

    // const pathNew =
    //   item.path_display.substring(0, item.path_display.lastIndexOf("/")) +
    //   "/" +
    //   nameNew;
    // this.dropbox
    //   .filesMove({
    //     from_path: item.path_display,
    //     to_path: pathNew,
    //   })
    //   .then((response) => {
    //     response.thumbUrl = item.thumbUrl;
    //     response.type = item.type;
    //     if (callback) callback(response);
    //   })
    //   .catch(function (error) {
    //     console.error(error);
    //   });
  };

  downloadItems = (items, callback) => {
    let dowloadStep = (itemIndex) => {
      if (itemIndex < items.length) {
        this._downloadItem(items[itemIndex], () => {
          dowloadStep(itemIndex + 1);
        });
      } else {
        window.Spinner.hide();
        //end
      }
    };

    window.Spinner.show();
    dowloadStep(0);
  };

  /**
   *
   * Download a list of items from dropbox
   *
   * @param {String[]} items list of items to be downloaded
   *
   * @returns A list of blobs, corresponding to the items provided.
   */
  downloadAllItems = async (items) => {
    let result = [];
    for (let item of items) {
      if (item.type == MultimediaHelper.TYPE_YOUTUBE) {
        continue;
      }
      const response = await this.dropbox.filesDownload({
        path: item.path_display,
      });
      result.push(
        new File([response.fileBlob], response.name, {
          type: "image/jpeg",
        })
      );
    }
    return result;
  };

  _downloadItem = (item, callback) => {
    if (item.type == MultimediaHelper.TYPE_YOUTUBE) {
      callback();
      return;
    }

    this.dropbox
      .filesDownload({
        path: item.path_display,
      })
      .then((response) => {
        var downloadUrl = URL.createObjectURL(response.fileBlob);
        var downloadButton = document.createElement("a");
        downloadButton.setAttribute("href", downloadUrl);
        downloadButton.setAttribute("download", response.name);
        downloadButton.setAttribute("class", "button");
        downloadButton.innerText = "Download: " + response.name;
        downloadButton.click();

        callback();
      })
      .catch((error) => {
        console.error(error);
      });
  };

  showMobileFullscreen(imageItem) {
    if (
      window.multimediaMobileHelper.isActive &&
      this.type == MultimediaHelper.FOLDER_MULTIMEDIA
    ) {
      window.multimediaMobileHelper.showFullScreen(
        this.userId,
        this.horseId,
        imageItem
      );
      return true;
    }
    return false;
  }

  downloadAndShareItems(items) {
    if (window.multimediaMobileHelper.isActive) {
      window.multimediaMobileHelper.downloadAndShareItems(items);
    }
  }

  hasImages = (callback) => {
    this.path = `${MultimediaHelper.FOLDER_MULTIMEDIA}=${this.horseId}`;
    this.dropbox
      .filesListFolder({ path: this.path })
      .then((response) => {
        if (callback) callback(response.entries.length > 0);
      })
      .catch((error) => {
        if (callback) callback(false);
      });
  };

  static getTypeItem(item) {
    let ext = item.path_display.split(".").pop().toLowerCase();
    if (
      ext == "png" ||
      ext == "jpeg" ||
      ext == "jpg" ||
      ext == "gif" ||
      ext == "bmp"
    ) {
      return MultimediaHelper.TYPE_IMAGE;
    } else if (ext == "pdf") {
      return MultimediaHelper.TYPE_PDF;
    } else {
      return MultimediaHelper.TYPE_VIDEO;
    }
  }
}
